import { useMutation, useQueryClient } from "@tanstack/react-query";
import { socialApi } from "@bw/shared/api";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * Mark-conversation-read mutation (Slice F2, WRITE). Posts
 * `markConversationRead(conversationId, messageIds)` for one rendered page and,
 * on success, cancels an older in-flight `social.conversations()` read and then
 * awaits its authoritative refetch. The inbox's unread_count therefore drops to
 * server truth before the read command is considered complete (never an
 * optimistic local zero).
 *
 * The page submits each distinct rendered boundary once: an unchanged poll or
 * re-render does not repeat the write, while a newly rendered message creates a
 * new boundary. Boundary de-duplication is the page's concern; this hook performs
 * one POST for a non-empty page batch in a `mutate()` call.
 */
export function useMarkRead(conversationId: string, communityId?: string) {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, string[]>({
    mutationFn: async (messageIds) => {
      if (messageIds.length > 0) {
        await socialApi.markConversationRead(conversationId, messageIds);
      }
    },
    onSuccess: async () => {
      const queryKey = communityId
        ? queryKeys.social.communityChannels(communityId)
        : queryKeys.social.conversations();
      // A list read started before the POST can otherwise finish after it and
      // reinstall a stale unread badge. Cancel it before requesting the
      // server-confirmed projection.
      await queryClient.cancelQueries({
        queryKey,
        exact: true,
      });
      await queryClient.invalidateQueries({
        queryKey,
      });
    },
  });
}
